import { useState, useCallback, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from './redux.hooks';
import {
  generalSelector,
  getExcludedLocations,
  excludeLocation,
  removeExcludedLocation,
  getLocations,
} from '../store/slices';
import { IExcludeLocationPayload, ILocation } from '../types';
import { toast } from 'react-toastify';

export const useLocationExclusions = () => {
  const dispatch = useAppDispatch();
  const { loading, excludedLocations, locations } =
    useAppSelector(generalSelector);

  const [selectedAreaFilter, setSelectedAreaFilter] = useState<string>('');
  const [selectedArea, setSelectedArea] = useState<string>('');
  const [selectedBrandId, setSelectedBrandId] = useState<number | undefined>(
    undefined
  );
  const [selectedBranchId, setSelectedBranchId] = useState<string | undefined>(
    undefined
  );
  const [areasWithinRadius, setAreasWithinRadius] = useState<string[]>([]);
  const [tabIndex, setTabIndex] = useState<number>(0); // 0 for brand exclusions, 1 for branch exclusions

  // Get all available areas from locations
  useEffect(() => {
    if (locations.length === 0) {
      dispatch(getLocations());
    }
  }, [dispatch, locations.length]);

  // Extract unique areas from locations
  const uniqueAreas = useCallback(() => {
    return Array.from(
      new Set(locations.map((location: ILocation) => location.area))
    );
  }, [locations]);

  // Fetch excluded locations based on brand or branch
  const fetchExcludedLocations = useCallback(
    (brandId?: number, branchId?: string) => {
      dispatch(getExcludedLocations({ brandId, branchId }));
    },
    [dispatch]
  );

  // Handle adding an area exclusion
  const handleAddExclusion = useCallback(async () => {
    if (!selectedArea) {
      toast.error('Please select an area to exclude');
      return;
    }

    // For brand tab (index 0), only require brandId
    if (tabIndex === 1 && !selectedBrandId) {
      toast.error('Please select a brand');
      return;
    }

    // For branch tab (index 1), require both brandId and branchId
    if (tabIndex === 0 && (!selectedBrandId || !selectedBranchId)) {
      toast.error('Please select a brand and branch');
      return;
    }

    const payload: IExcludeLocationPayload = {
      area: selectedArea,
      brandId: selectedBrandId,
      branchId: selectedBranchId,
    };

    try {
      await dispatch(excludeLocation(payload)).unwrap();
      toast.success('Area excluded successfully');
      setSelectedArea('');
    } catch (error: any) {
      toast.error(error.message || 'Failed to exclude area');
    }
  }, [dispatch, selectedArea, selectedBrandId, selectedBranchId, tabIndex]);

  // Handle removing an area exclusion
  const handleRemoveExclusion = useCallback(
    async (id: number) => {
      try {
        await dispatch(
          removeExcludedLocation({
            id,
            brandId: selectedBrandId,
            branchId: selectedBranchId,
          })
        ).unwrap();
        toast.success('Exclusion removed successfully');
      } catch (error: any) {
        toast.error(error.message || 'Failed to remove exclusion');
      }
    },
    [dispatch, selectedBrandId, selectedBranchId]
  );

  // Calculate areas within a radius of a brand/branch
  const calculateAreasWithinRadius = useCallback(
    (latitude: number, longitude: number, radiusKm: number) => {
      // This is a simplified method. In production, you'd need a more accurate
      // geospatial calculation using the Haversine formula or a similar algorithm.
      const areas = locations
        .filter((location: any) => {
          // Simplified distance calculation between two points
          const distance =
            Math.sqrt(
              Math.pow(location.latitude - latitude, 2) +
                Math.pow(location.longitude - longitude, 2)
            ) * 111; // rough conversion to km

          return distance <= radiusKm;
        })
        .map((location: ILocation) => location.area);

      setAreasWithinRadius(Array.from(new Set(areas)));
    },
    [locations]
  );

  // Filter exclusions based on area filter
  const filteredExclusions = selectedAreaFilter
    ? excludedLocations.filter((exclusion) =>
        exclusion.area.toLowerCase().includes(selectedAreaFilter.toLowerCase())
      )
    : excludedLocations;

  // Filter brand or branch exclusions based on tab
  const brandExclusions = excludedLocations.filter(
    (exclusion) => exclusion.brandId
  );
  const branchExclusions = excludedLocations.filter(
    (exclusion) => exclusion.branchId
  );

  return {
    loading,
    excludedLocations,
    filteredExclusions,
    brandExclusions,
    branchExclusions,
    selectedAreaFilter,
    setSelectedAreaFilter,
    selectedArea,
    setSelectedArea,
    selectedBrandId,
    setSelectedBrandId,
    selectedBranchId,
    setSelectedBranchId,
    tabIndex,
    setTabIndex,
    areasWithinRadius,
    locations,
    uniqueAreas,
    fetchExcludedLocations,
    handleAddExclusion,
    handleRemoveExclusion,
    calculateAreasWithinRadius,
  };
};
